import { useId, useRef, useState, type ReactNode } from 'react';
import { useEscapeToClose } from '../lib/dialog-stack';
import { useDialogFocus } from '../lib/focus-trap';
import { cn } from '../lib/utils';
import { Icon } from './icons';

/** The ONE content width for all three apps (container-max, 1200px) with the
 * design gutters: 16px on a phone, 24px from md. Header, banner, room header and
 * page body all use it so their edges line up. */
export const containerClass = 'mx-auto w-full max-w-container px-4 md:px-6';

export type NavVariant = 'bar' | 'drawer';

/** The page's ONE h1 (room or page name), in the serif page-title style. */
export function PageTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h1
      className={cn(
        'break-words font-serif text-2xl font-semibold leading-8 tracking-tight text-balance md:text-[28px] md:leading-9',
        className,
      )}
    >
      {children}
    </h1>
  );
}

/** Class for a top-level nav link in the header bar or the mobile drawer. */
export function navLinkClass(variant: NavVariant, active: boolean): string {
  return variant === 'bar'
    ? cn(
        'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-sm font-medium transition-colors hover:bg-surface-2 hover:text-ink',
        active ? 'bg-surface-2 text-ink' : 'text-ink-2',
      )
    : cn(
        'flex min-h-tap w-full items-center gap-3 rounded-md px-3 text-left text-sm font-medium',
        active ? 'bg-primary-soft text-primary-ink' : 'text-ink hover:bg-surface-2',
      );
}

/** The ClosingVault brand accent (matches the marketing mark). */
const BRAND_ACCENT = '#FF6A2B';

/** The wordmark: the ClosingVault vault-door glyph (a rounded square holding a
 * dial — a circle with four short spokes, as on the marketing mark) +
 * "ClosingVault" in plain type + the app's name. Put it inside the app's home
 * link. */
export function BrandMark({ appLabel }: { appLabel?: string }) {
  return (
    <span className="inline-flex items-center gap-2.5 text-base font-bold tracking-tight text-ink">
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
        width="24"
        height="24"
        className="h-6 w-6 shrink-0"
      >
        <rect width="24" height="24" rx="5" fill={BRAND_ACCENT} />
        <circle cx="12" cy="12" r="5" fill="none" stroke="#fff" strokeWidth="2" />
        <path
          d="M12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      ClosingVault
      {appLabel ? <span className="text-sm font-medium text-ink-3">{appLabel}</span> : null}
    </span>
  );
}

export interface AppShellProps {
  /** The home link (the app wraps {@link BrandMark} in its router Link). */
  brand: ReactNode;
  /** The app's top-level nav, rendered as a bar (≥768px) or in the drawer.
   * `close` dismisses the drawer — call it from each drawer link's onClick. */
  renderNav?: (variant: NavVariant, close: () => void) => ReactNode;
  /** Secondary header actions (help, sign out). Inline ≥768px; in the drawer below. */
  renderActions?: (variant: NavVariant, close: () => void) => ReactNode;
  /** A full-bleed strip under the header (the trial banner). */
  banner?: ReactNode;
  children: ReactNode;
}

/**
 * The shared app shell (DataRoom UI "AppShell"): a sticky 56px header with the
 * wordmark, the app nav and secondary actions, inside the one 1200px container.
 * Below 768px the nav and actions collapse behind a 44px menu button that opens
 * a right-hand drawer — a modal dialog: scrim behind, focus moved in and trapped,
 * Escape / the close button / the scrim close it, and focus returns to the menu
 * button. Nothing in the header can push the page wider than the phone.
 */
export function AppShell({ brand, renderNav, renderActions, banner, children }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const close = () => setDrawerOpen(false);
  const hasMenu = Boolean(renderNav || renderActions);

  return (
    <div className="min-h-screen bg-canvas">
      <header className="sticky top-0 z-30 h-header border-b bg-surface">
        <div className={cn(containerClass, 'flex h-full items-center gap-4 lg:gap-6')}>
          <div className="min-w-0 shrink-0">{brand}</div>
          {renderNav ? (
            <nav aria-label="Main" className="hidden flex-1 items-center gap-1 md:flex">
              {renderNav('bar', close)}
            </nav>
          ) : (
            <div className="hidden flex-1 md:block" />
          )}
          {renderActions ? (
            <div className="ml-auto hidden items-center gap-2 md:flex">
              {renderActions('bar', close)}
            </div>
          ) : null}
          {hasMenu ? (
            <button
              type="button"
              aria-label="Open menu"
              aria-haspopup="dialog"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
              className="ml-auto inline-flex h-11 w-11 items-center justify-center rounded-md text-ink hover:bg-surface-2 md:hidden"
            >
              <Icon name="menu" size={20} />
            </button>
          ) : null}
        </div>
      </header>
      {banner}
      {children}
      {drawerOpen ? (
        <NavDrawer brand={brand} onClose={close}>
          {renderNav ? (
            <nav aria-label="Main" className="flex flex-col gap-1 p-2">
              {renderNav('drawer', close)}
            </nav>
          ) : null}
          {renderActions ? (
            <div className="flex flex-col gap-1 border-t p-2">{renderActions('drawer', close)}</div>
          ) : null}
        </NavDrawer>
      ) : null}
    </div>
  );
}

function NavDrawer({
  brand,
  onClose,
  children,
}: {
  brand: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEscapeToClose(onClose);
  useDialogFocus(panelRef);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-scrim md:hidden" onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="flex h-full w-[min(320px,86vw)] flex-col overflow-y-auto bg-surface shadow-lg focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-header shrink-0 items-center justify-between border-b px-4">
          <span id={titleId} className="sr-only">
            Menu
          </span>
          <div className="min-w-0" onClick={onClose}>
            {brand}
          </div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-ink hover:bg-surface-2"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
